import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { posthog, posthogLoaded } from "@/lib/posthog";

import { CitySelector } from "@/components/layout/CitySelector";
import { LanguageSelector } from "@/components/layout/LanguageSelector";
import { useCity } from "@/contexts/CityContext";
import { CITIES } from "@/seo/shared";
import { LANG_LABELS, LANG_SHORT, SUPPORTED_LANGS, normalizeLang, type Lang } from "@/i18n/config";
import { cn } from "@/lib/utils";

const LOGO_BLACK =
  "https://asrgzcggnwnsvpcsmxxw.supabase.co/storage/v1/object/public/media/brand/RECINE_black.svg?v=2";
const LOGO_WHITE =
  "https://asrgzcggnwnsvpcsmxxw.supabase.co/storage/v1/object/public/media/brand/RECINE_white.svg?v=2";

/** Exploración: la cartelera se parte en sus dos vistas, «Horarios» y
 *  «Películas» (antes, un interruptor dentro de los filtros). Llevan a la
 *  cartelera de la ciudad elegida, o a la general si no hay ninguna. */
function useNavLinks() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { city } = useCity();
  const cityDef = CITIES.find((c) => c.name === city);
  const base = cityDef ? `/${cityDef.slug}` : "/peliculas";
  // Cartelera: /peliculas, /{ciudad} y sus landings (/{ciudad}/hoy…), no las fichas.
  const onListing =
    pathname === "/peliculas" ||
    CITIES.some((c) => pathname === `/${c.slug}` || (pathname.startsWith(`/${c.slug}/`) && !pathname.includes("/pelicula/")));
  // «Próximamente» va por película: cuenta como «Películas».
  const moviesView = new URLSearchParams(search).get("vista") === "peliculas" || pathname.endsWith("/proximamente");
  return [
    { to: base, label: t("nav.horarios"), active: onListing && !moviesView },
    { to: `${base}?vista=peliculas`, label: t("nav.peliculas"), active: onListing && moviesView },
    { to: "/cines", label: t("footer.cines"), active: pathname.startsWith("/cines") },
    { to: "/suscripcion", label: t("nav.subscription"), active: pathname === "/suscripcion" },
  ];
}

// Pastilla lima con borde azul marino, como el resto de botones principales
// (1-11-2026: antes en minúsculas y con el color invertido al pasar). Al pasar,
// todos los botones lima se levantan con una sombra dura azul marino.
const loginCls =
  "data-voice inline-flex shrink-0 items-center whitespace-nowrap rounded-full border-2 border-rc-navy bg-rc-lime font-bold text-rc-navy transition-[transform,box-shadow] duration-200 ease-spring hover:-translate-x-[3px] hover:-translate-y-[3px] hover:shadow-hard-sm active:translate-x-0 active:translate-y-0 active:shadow-none active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet focus-visible:ring-offset-2";

/**
 * Cabecera pública. Desde 1024 px, la barra completa. Por debajo no cabe (a
 * 375 px se salía por los dos lados: el logo y el botón de iniciar sesión
 * quedaban cortados, 2026-10-16): queda logo + ciudad + iniciar sesión +
 * hamburguesa, y el resto va a un panel a pantalla completa. Mismo patrón que
 * la Navigation de RE:PÚBLICO: el logo encoge (min-w-0) y lo de la derecha no.
 */
export function Header({ transparent = true }: { transparent?: boolean }) {
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = useNavLinks();

  // Cerrar el panel al navegar; Esc lo cierra y mientras está abierto la
  // página de debajo no hace scroll.
  // También con solo la búsqueda (?vista=peliculas) o el mismo enlace: antes
  // se cerraba solo al cambiar de ruta y «Películas» desde /valencia lo dejaba
  // abierto (31-10-2026).
  useEffect(() => setMenuOpen(false), [pathname, search]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  // Exploración: cabecera siempre en banda lima (también sobre la portada).
  const solid = true;
  void transparent;
  const currentLang = normalizeLang(i18n.language);
  const chooseLang = (lng: Lang) => {
    if (posthogLoaded() && lng !== currentLang) posthog().capture("language_changed", { lang: lng });
    void i18n.changeLanguage(lng);
  };

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-40 transition-[background,backdrop-filter,border-color] duration-300",
        solid
          ? "bg-white border-b-2 border-rc-navy"
          : "border-b border-transparent"
      )}
    >
      <div className="w-full px-4 sm:px-6 md:px-14 flex items-center justify-between gap-3 h-20">
        <Link
          to="/"
          className={cn(
            "flex min-w-0 items-center transition-opacity duration-200 ease-spring hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded active:scale-95",
            // Foco: violeta sobre la barra blanca, lima sobre la foto de la portada.
            solid ? "focus-visible:ring-rc-violet" : "focus-visible:ring-rc-lime"
          )}
          aria-label="RE:CINE — inicio"
        >
          <img
            src={solid ? LOGO_BLACK : LOGO_WHITE}
            alt="RE:CINE"
            width={132}
            height={40}
            className="h-8 sm:h-10 md:h-12 xl:h-14 w-auto min-w-0 object-contain object-left"
          />
        </Link>

        {/* Escritorio */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-10">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              aria-current={l.active ? "page" : undefined}
              className={cn(
                "data-voice text-base font-bold transition-colors duration-200 px-1 py-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-95",
                solid ? "focus-visible:ring-rc-violet" : "focus-visible:ring-rc-lime",
                solid
                  ? l.active
                    ? "text-rc-navy underline decoration-2 underline-offset-[6px]"
                    : "text-rc-navy hover:underline hover:decoration-2 hover:underline-offset-[6px]"
                  : // Sobre la foto de la portada, todo en blanco puro y la
                    // activa subrayada, como en la barra blanca (1-11-2026).
                    l.active
                    ? "text-white underline decoration-2 underline-offset-[6px]"
                    : "text-white hover:underline hover:decoration-2 hover:underline-offset-[6px]"
              )}
            >
              {l.label}
            </Link>
          ))}

          <CitySelector solid={solid} />

          {/* Puerta de acceso en recine.es: sólo socios existentes. Los nuevos
              caen en la lista de espera; los socios reciben el magic link que
              aterriza en mi.recine.es. */}
          <Link to="/login" className={cn(loginCls, "px-5 py-2.5 text-xs")}>
            {t("nav.login")}
          </Link>

          {/* Exploración: el idioma, al otro lado de «Iniciar sesión» (como en RE:PÚBLICO). */}
          <LanguageSelector solid={solid} />
        </nav>

        {/* Móvil y tablet */}
        <div className="flex shrink-0 items-center gap-1 lg:hidden">
          <CitySelector solid={solid} />
          <Link to="/login" className={cn(loginCls, "px-3.5 py-1.5 text-[11px]")}>
            {t("nav.login")}
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={t("nav.openMenu")}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className={cn(
              "-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 active:scale-95",
              solid ? "text-rc-navy hover:bg-rc-navy/5 focus-visible:ring-rc-violet" : "text-white hover:bg-white/10 focus-visible:ring-rc-lime"
            )}
          >
            <MenuIcon />
          </button>
        </div>
      </div>

      {/* El panel va a <body>: el backdrop-blur de la cabecera haría que un
          `fixed` dentro de ella se posicione respecto a la cabecera. */}
      {menuOpen &&
        createPortal(
          <div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            data-state="open"
            aria-label={t("nav.menu")}
            className="fixed inset-0 z-50 flex min-h-[100dvh] flex-col overflow-y-auto bg-rc-violet text-white lg:hidden"
          >
            <div className="flex h-20 shrink-0 items-center justify-between px-4 sm:px-6">
              <Link to="/" className="flex min-w-0 items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime">
                <img src={LOGO_WHITE} alt="RE:CINE" width={132} height={40} className="h-10 sm:h-12 w-auto" />
              </Link>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label={t("nav.closeMenu")}
                autoFocus
                className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime active:scale-95"
              >
                <CloseIcon />
              </button>
            </div>

            <nav className="flex flex-1 flex-col px-6 pt-4 sm:px-8">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMenuOpen(false)}
                  aria-current={l.active ? "page" : undefined}
                  className={cn(
                    // En mayúsculas, como en escritorio (31-10-2026).
                    "py-3 font-display text-3xl uppercase tracking-[-0.01em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime rounded",
                    l.active ? "text-rc-lime" : "text-white hover:text-rc-lime"
                  )}
                >
                  {l.label}
                </Link>
              ))}

              <Link to="/login" onClick={() => setMenuOpen(false)} className={cn(loginCls, "mt-8 self-start px-6 py-1.5 text-base")}>
                {t("nav.login")}
              </Link>

              {/* Idiomas en corto, como en el menú de RE:PÚBLICO: el elegido en
                  lima y subrayado (31-10-2026). */}
              <div
                role="group"
                aria-label={t("nav.language")}
                className="mt-10 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/15 pt-6 pb-10"
              >
                {SUPPORTED_LANGS.map((lng) => {
                  const active = lng === currentLang;
                  return (
                    <button
                      key={lng}
                      type="button"
                      lang={lng}
                      onClick={() => chooseLang(lng)}
                      aria-pressed={active}
                      aria-label={LANG_LABELS[lng]}
                      className={cn(
                        "rounded py-1 font-display text-2xl tracking-[-0.01em] decoration-2 underline-offset-[6px] transition-[color,transform] duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-lime active:scale-95",
                        active ? "text-rc-lime underline" : "text-white hover:text-rc-lime"
                      )}
                    >
                      {LANG_SHORT[lng]}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>,
          document.body
        )}
    </header>
  );
}

function MenuIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
